import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext";
import { useWorkshop } from "../../contexts/WorkshopContext";
import {
  search,
  getSearchHistory,
  addToSearchHistory,
  removeFromSearchHistory,
  clearSearchHistory,
  SearchResult,
} from "../../services/searchService";

interface SearchBarProps {
  placeholder?: string;
  autoFocus?: boolean;
  compact?: boolean; // New prop for compact mode
}

const SearchBar: React.FC<SearchBarProps> = ({
  placeholder = "Search inspections, vehicles...",
  autoFocus = false,
  compact = false,
}) => {
  const { isDarkMode } = useTheme();
  const { selectedWorkshop } = useWorkshop();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [history, setHistory] = useState<string[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // Load search history on mount
  useEffect(() => {
    setHistory(getSearchHistory());
  }, []);

  // Auto-focus if requested
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        if (compact && !query) {
          setIsExpanded(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [compact, query]);

  // Debounced search
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const timer = setTimeout(async () => {
      try {
        // Pass workshop ID to filter results
        const response = await search(query, selectedWorkshop?.id);
        setResults(response.results);
        setSelectedIndex(-1);
      } catch (error) {
        console.error("Search error:", error);
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query, selectedWorkshop]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    setIsOpen(true);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
    setIsExpanded(true);
    if (!query.trim()) {
      setHistory(getSearchHistory());
    }
  };

  const handleSelectResult = (result: SearchResult) => {
    addToSearchHistory(query);
    setQuery("");
    setIsOpen(false);

    if (result.type === "inspection") {
      navigate(`/inspections/${result.id}/details`);
    } else if (result.type === "vehicle") {
      navigate(`/vehicles?id=${result.id}`);
    }
  };

  const handleSelectHistory = (historyQuery: string) => {
    setQuery(historyQuery);
    setIsOpen(true);
    inputRef.current?.focus();
  };

  const handleRemoveHistory = (historyQuery: string, e: React.MouseEvent) => {
    e.stopPropagation();
    removeFromSearchHistory(historyQuery);
    setHistory(getSearchHistory());
  };

  const handleClearHistory = () => {
    clearSearchHistory();
    setHistory([]);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const totalItems = results.length + (query.trim() ? 0 : history.length);

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev < totalItems - 1 ? prev + 1 : prev));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === "Enter" && selectedIndex >= 0) {
      e.preventDefault();
      if (query.trim() && results[selectedIndex]) {
        handleSelectResult(results[selectedIndex]);
      } else if (!query.trim() && history[selectedIndex]) {
        handleSelectHistory(history[selectedIndex]);
      }
    } else if (e.key === "Escape") {
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };

  const getResultIcon = (type: string) => {
    if (type === "inspection") {
      return (
        <svg
          className="w-5 h-5 text-blue-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
          />
        </svg>
      );
    }
    return (
      <svg
        className="w-5 h-5 text-gray-500"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    );
  };

  const getStatusBadge = (status?: string) => {
    if (!status) return null;

    const colors = {
      open: "bg-green-100 text-green-800",
      closed: "bg-gray-100 text-gray-800",
    };

    return (
      <span
        className={`px-2 py-0.5 text-xs font-medium rounded ${
          colors[status as keyof typeof colors] || "bg-gray-100 text-gray-800"
        }`}
      >
        {status.toUpperCase()}
      </span>
    );
  };

  const highlightMatch = (text: string, query: string) => {
    if (!query.trim()) return text;

    const regex = new RegExp(`(${query})`, "gi");
    const parts = text.split(regex);

    return (
      <>
        {parts.map((part, i) =>
          regex.test(part) ? (
            <mark key={i} className="bg-yellow-200 font-semibold">
              {part}
            </mark>
          ) : (
            <span key={i}>{part}</span>
          )
        )}
      </>
    );
  };

  const showResults = query.trim() && results.length > 0;
  const showHistory = !query.trim() && history.length > 0;
  const showNoResults = query.trim() && !isLoading && results.length === 0;

  // Compact mode rendering
  if (compact) {
    return (
      <div
        ref={searchRef}
        className="relative"
        onMouseEnter={() => setIsExpanded(true)}
        onMouseLeave={() => !query && !isOpen && setIsExpanded(false)}
      >
        {/* Search Button/Input Container */}
        <div
          className={`flex items-center transition-all duration-300 ease-in-out ${
            isExpanded ? "w-64" : "w-10"
          }`}
        >
          {/* Search Icon Button */}
          <button
            onClick={() => {
              setIsExpanded(true);
              inputRef.current?.focus();
            }}
            className={`flex-shrink-0 p-2 rounded-lg transition-all ${
              isDarkMode
                ? "bg-gray-700/50 hover:bg-gray-700 text-gray-300"
                : "bg-gray-100 hover:bg-gray-200 text-gray-700"
            }`}
          >
            <svg
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </button>

          {/* Expandable Input */}
          <div
            className={`overflow-hidden transition-all duration-300 ease-in-out ${
              isExpanded ? "w-full ml-2 opacity-100" : "w-0 opacity-0"
            }`}
          >
            <div className="relative">
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                className={`block w-full pl-3 pr-8 py-2 border rounded-lg text-sm focus:outline-none transition-all ${
                  isDarkMode
                    ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-phoenix-500"
                    : "bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-phoenix-500"
                }`}
              />
              {query && (
                <button
                  onClick={() => {
                    setQuery("");
                    setResults([]);
                    inputRef.current?.focus();
                  }}
                  className={`absolute inset-y-0 right-0 pr-2 flex items-center ${
                    isDarkMode
                      ? "text-gray-400 hover:text-gray-300"
                      : "text-gray-400 hover:text-gray-600"
                  }`}
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
              {isLoading && (
                <div className="absolute inset-y-0 right-0 pr-2 flex items-center">
                  <div className="animate-spin h-4 w-4 border-2 border-phoenix-500 border-t-transparent rounded-full"></div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Dropdown Results - Same as regular mode */}
        {isOpen &&
          isExpanded &&
          (showResults || showHistory || showNoResults) && (
            <div
              className={`absolute z-50 right-0 w-80 mt-2 rounded-lg shadow-xl border overflow-hidden max-h-96 overflow-y-auto ${
                isDarkMode
                  ? "bg-gray-800 border-gray-700"
                  : "bg-white border-gray-200"
              }`}
            >
              {/* Search Results */}
              {showResults && (
                <div>
                  <div
                    className={`px-4 py-2 text-xs font-semibold border-b ${
                      isDarkMode
                        ? "text-gray-400 bg-gray-900 border-gray-700"
                        : "text-gray-500 bg-gray-50 border-gray-200"
                    }`}
                  >
                    Search Results ({results.length})
                  </div>
                  {results.map((result, index) => (
                    <button
                      key={`${result.type}-${result.id}`}
                      onClick={() => handleSelectResult(result)}
                      className={`w-full px-4 py-3 text-left transition-colors border-b last:border-b-0 ${
                        isDarkMode
                          ? "hover:bg-gray-700 border-gray-700"
                          : "hover:bg-gray-50 border-gray-100"
                      } ${
                        selectedIndex === index
                          ? isDarkMode
                            ? "bg-gray-700"
                            : "bg-blue-50"
                          : ""
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5">
                          {getResultIcon(result.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h4
                              className={`text-sm font-medium truncate ${
                                isDarkMode ? "text-gray-200" : "text-gray-900"
                              }`}
                            >
                              {highlightMatch(result.title, query)}
                            </h4>
                            {result.metadata?.status &&
                              getStatusBadge(result.metadata.status)}
                          </div>
                          <p
                            className={`text-sm mt-0.5 ${
                              isDarkMode ? "text-gray-400" : "text-gray-600"
                            }`}
                          >
                            {highlightMatch(result.subtitle, query)}
                          </p>
                          {result.metadata?.created_at && (
                            <p
                              className={`text-xs mt-1 ${
                                isDarkMode ? "text-gray-500" : "text-gray-500"
                              }`}
                            >
                              {new Date(
                                result.metadata.created_at
                              ).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                        {result.metadata?.progress !== undefined && (
                          <div className="text-xs font-semibold text-blue-600">
                            {result.metadata.progress}%
                          </div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Search History */}
              {showHistory && (
                <div>
                  <div
                    className={`px-4 py-2 text-xs font-semibold border-b flex items-center justify-between ${
                      isDarkMode
                        ? "text-gray-400 bg-gray-900 border-gray-700"
                        : "text-gray-500 bg-gray-50 border-gray-200"
                    }`}
                  >
                    <span>Recent Searches</span>
                    <button
                      onClick={handleClearHistory}
                      className={`font-normal ${
                        isDarkMode
                          ? "text-orange-400 hover:text-orange-300"
                          : "text-blue-600 hover:text-blue-700"
                      }`}
                    >
                      Clear All
                    </button>
                  </div>
                  {history.map((historyQuery, index) => (
                    <button
                      key={index}
                      onClick={() => handleSelectHistory(historyQuery)}
                      className={`w-full px-4 py-3 text-left transition-colors border-b last:border-b-0 group ${
                        isDarkMode
                          ? "hover:bg-gray-700 border-gray-700"
                          : "hover:bg-gray-50 border-gray-100"
                      } ${
                        selectedIndex === index
                          ? isDarkMode
                            ? "bg-gray-700"
                            : "bg-blue-50"
                          : ""
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <svg
                          className={`w-4 h-4 ${
                            isDarkMode ? "text-gray-500" : "text-gray-400"
                          }`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <span
                          className={`flex-1 text-sm ${
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          {historyQuery}
                        </span>
                        <button
                          onClick={(e) => handleRemoveHistory(historyQuery, e)}
                          className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-red-600"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* No Results */}
              {showNoResults && (
                <div className="px-4 py-8 text-center">
                  <svg
                    className={`w-12 h-12 mx-auto mb-3 ${
                      isDarkMode ? "text-gray-600" : "text-gray-300"
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <p
                    className={`text-sm ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    No results found for "
                    <span className="font-semibold">{query}</span>"
                  </p>
                  <p
                    className={`text-xs mt-1 ${
                      isDarkMode ? "text-gray-500" : "text-gray-400"
                    }`}
                  >
                    Try searching by inspection ID, vehicle number, or status
                  </p>
                </div>
              )}
            </div>
          )}
      </div>
    );
  }

  // Regular mode rendering (for non-compact usage)
  return (
    <div ref={searchRef} className="relative w-full">
      {/* Search Input */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <svg
            className="h-5 w-5 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={`block w-full pl-10 pr-10 py-2 border rounded-lg text-sm focus:outline-none transition-all ${
            isDarkMode
              ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-phoenix-500"
              : "bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-phoenix-500"
          }`}
        />
        {query && (
          <button
            onClick={() => {
              setQuery("");
              setResults([]);
              inputRef.current?.focus();
            }}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
        {isLoading && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
            <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
          </div>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && (showResults || showHistory || showNoResults) && (
        <div
          className={`absolute z-50 w-full mt-2 rounded-lg shadow-xl border overflow-hidden max-h-96 overflow-y-auto ${
            isDarkMode
              ? "bg-gray-800 border-gray-700"
              : "bg-white border-gray-200"
          }`}
        >
          {/* Search Results */}
          {showResults && (
            <div>
              <div
                className={`px-4 py-2 text-xs font-semibold border-b ${
                  isDarkMode
                    ? "text-gray-400 bg-gray-900 border-gray-700"
                    : "text-gray-500 bg-gray-50 border-gray-200"
                }`}
              >
                Search Results ({results.length})
              </div>
              {results.map((result, index) => (
                <button
                  key={`${result.type}-${result.id}`}
                  onClick={() => handleSelectResult(result)}
                  className={`w-full px-4 py-3 text-left transition-colors border-b last:border-b-0 ${
                    isDarkMode
                      ? "hover:bg-gray-700 border-gray-700"
                      : "hover:bg-gray-50 border-gray-100"
                  } ${
                    selectedIndex === index
                      ? isDarkMode
                        ? "bg-gray-700"
                        : "bg-blue-50"
                      : ""
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5">{getResultIcon(result.type)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4
                          className={`text-sm font-medium truncate ${
                            isDarkMode ? "text-gray-200" : "text-gray-900"
                          }`}
                        >
                          {highlightMatch(result.title, query)}
                        </h4>
                        {result.metadata?.status &&
                          getStatusBadge(result.metadata.status)}
                      </div>
                      <p
                        className={`text-sm mt-0.5 ${
                          isDarkMode ? "text-gray-400" : "text-gray-600"
                        }`}
                      >
                        {highlightMatch(result.subtitle, query)}
                      </p>
                      {result.metadata?.created_at && (
                        <p
                          className={`text-xs mt-1 ${
                            isDarkMode ? "text-gray-500" : "text-gray-500"
                          }`}
                        >
                          {new Date(
                            result.metadata.created_at
                          ).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    {result.metadata?.progress !== undefined && (
                      <div className="text-xs font-semibold text-blue-600">
                        {result.metadata.progress}%
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Search History */}
          {showHistory && (
            <div>
              <div
                className={`px-4 py-2 text-xs font-semibold border-b flex items-center justify-between ${
                  isDarkMode
                    ? "text-gray-400 bg-gray-900 border-gray-700"
                    : "text-gray-500 bg-gray-50 border-gray-200"
                }`}
              >
                <span>Recent Searches</span>
                <button
                  onClick={handleClearHistory}
                  className={`font-normal ${
                    isDarkMode
                      ? "text-orange-400 hover:text-orange-300"
                      : "text-blue-600 hover:text-blue-700"
                  }`}
                >
                  Clear All
                </button>
              </div>
              {history.map((historyQuery, index) => (
                <button
                  key={index}
                  onClick={() => handleSelectHistory(historyQuery)}
                  className={`w-full px-4 py-3 text-left transition-colors border-b last:border-b-0 group ${
                    isDarkMode
                      ? "hover:bg-gray-700 border-gray-700"
                      : "hover:bg-gray-50 border-gray-100"
                  } ${
                    selectedIndex === index
                      ? isDarkMode
                        ? "bg-gray-700"
                        : "bg-blue-50"
                      : ""
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <svg
                      className={`w-4 h-4 ${
                        isDarkMode ? "text-gray-500" : "text-gray-400"
                      }`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span
                      className={`flex-1 text-sm ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      {historyQuery}
                    </span>
                    <button
                      onClick={(e) => handleRemoveHistory(historyQuery, e)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-red-600"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* No Results */}
          {showNoResults && (
            <div className="px-4 py-8 text-center">
              <svg
                className={`w-12 h-12 mx-auto mb-3 ${
                  isDarkMode ? "text-gray-600" : "text-gray-300"
                }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p
                className={`text-sm ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                No results found for "
                <span className="font-semibold">{query}</span>"
              </p>
              <p
                className={`text-xs mt-1 ${
                  isDarkMode ? "text-gray-500" : "text-gray-400"
                }`}
              >
                Try searching by inspection ID, vehicle number, or status
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
